/**
 * Modal Amélioration Continue et Rigueur - 9 Standards d'Excellence BZ
 * Design motivant pour élever l'équipe
 */

import { X, BookOpen, Brain, TrendingUp, FileText, Rocket, Target, Zap, Lightbulb, Users, Award, CheckCircle, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface AméliorationContinueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AméliorationContinueModal({ isOpen, onClose }: AméliorationContinueModalProps) {
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['amelioration_continue_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  const standards = [
    {
      id: 1,
      title: "Documentation Rigoureuse",
      subtitle: "Notre Mémoire Collective",
      icon: BookOpen,
      color: "blue",
      gradient: "from-blue-500 to-cyan-500",
      description: "Documenter dans ERP/Servo/MonBZ n'est pas une corvée, c'est un investissement. Chaque note sauvée est une connaissance qui perdure.",
      examples: [
        "📝 Documenter CHAQUE intervention client",
        "🔍 Décrire le problème ET la solution",
        "⏱️ Temps investi = temps économisé pour l'équipe",
        "💡 Utiliser l'IA pour résumer les notes longues"
      ],
      impact: "Une documentation claire = zéro perte de temps à chercher l'historique",
      aiTip: "💡 Prompt IA: 'Transforme ces notes brutes en documentation client professionnelle et structurée'"
    },
    {
      id: 2,
      title: "Hygiène Prompt IA",
      subtitle: "L'Art de Bien Questionner",
      icon: Brain,
      color: "purple",
      gradient: "from-purple-500 to-pink-500",
      description: "L'IA est puissante, mais seulement si tu sais la questionner. Un bon prompt = des réponses 10x meilleures.",
      examples: [
        "❌ Mauvais: 'Comment configurer le wifi?'",
        "✅ Bon: 'Configuration wifi sécurisée pour cabinet comptable 25 users, Ubiquiti UniFi, segmentation VLAN invités'",
        "🎯 Sois spécifique, donne le contexte",
        "🔄 Raffine tes prompts jusqu'à l'excellence"
      ],
      impact: "Un prompt précis = solution directement applicable",
      aiTip: "💡 Toujours inclure: Contexte + Contraintes + Objectif + Format souhaité"
    },
    {
      id: 3,
      title: "Volonté d'Apprendre",
      subtitle: "Curiosité = Excellence",
      icon: Lightbulb,
      color: "yellow",
      gradient: "from-yellow-500 to-orange-500",
      description: "L'IA peut tout faire... si tu oses lui demander. Sans curiosité, tu passes à côté de 90% de son potentiel.",
      examples: [
        "🚀 Chaque jour, teste 1 nouveau prompt IA",
        "📚 Lis les docs, regarde des tutos",
        "💬 Demande comment l'IA peut t'aider sur tes dossiers",
        "🎓 Apprendre = grandir, stagner = reculer"
      ],
      impact: "Une équipe curieuse = une équipe imbattable",
      aiTip: "💡 Prompt IA: 'Donne-moi 5 façons d'utiliser l'IA pour optimiser [ton défi actuel]'"
    },
    {
      id: 4,
      title: "Logs Quotidiens",
      subtitle: "La Traçabilité qui Parle",
      icon: FileText,
      color: "green",
      gradient: "from-green-500 to-emerald-500",
      description: "Un log détaillé aujourd'hui = une analyse puissante demain. La rigueur dans les logs révèle les patterns.",
      examples: [
        "📊 Log TOUT: interventions, décisions, changements",
        "⏰ Horodatage précis = chronologie claire",
        "🔗 Lien vers tickets, clients, contexte",
        "📈 Plus de logs = plus d'insights IA"
      ],
      impact: "Logs rigoureux = Analyses pertinentes = Décisions éclairées",
      aiTip: "💡 Prompt IA: 'Analyse ces logs et identifie les patterns récurrents + recommandations d'amélioration'"
    },
    {
      id: 5,
      title: "Sortir de sa Zone de Confort",
      subtitle: "Grandir ou Stagner",
      icon: Rocket,
      color: "red",
      gradient: "from-red-500 to-pink-500",
      description: "La croissance commence là où le confort s'arrête. Chaque nouveau défi est une opportunité de briller.",
      examples: [
        "💪 Accepte les dossiers qui te challengent",
        "🆕 Apprends une nouvelle techno par trimestre",
        "🎤 Présente tes découvertes à l'équipe",
        "🔥 L'inconfort temporaire = compétences permanentes"
      ],
      impact: "Zone de confort élargie = Expert polyvalent",
      aiTip: "💡 Utilise l'IA comme mentor: 'Guide-moi étape par étape pour maîtriser [nouvelle compétence]'"
    },
    {
      id: 6,
      title: "Bilan de Dossiers",
      subtitle: "Apprendre de Chaque Projet",
      icon: Target,
      color: "indigo",
      gradient: "from-indigo-500 to-purple-500",
      description: "Chaque dossier terminé est une mine d'or d'apprentissage. Le bilan post-projet n'est pas optionnel, il est essentiel.",
      examples: [
        "✅ Qu'est-ce qui a bien fonctionné?",
        "❌ Qu'est-ce qui a mal tourné?",
        "💡 Quels apprentissages pour la prochaine fois?",
        "📝 Documente pour que l'équipe en profite"
      ],
      impact: "Équipe qui fait des bilans = Équipe qui ne répète pas ses erreurs",
      aiTip: "💡 Prompt IA: 'Fais un post-mortem structuré de ce projet: succès, échecs, leçons, recommandations'"
    },
    {
      id: 7,
      title: "Mentalité Kaizen",
      subtitle: "1% Mieux Chaque Jour",
      icon: TrendingUp,
      color: "teal",
      gradient: "from-teal-500 to-cyan-500",
      description: "Tu n'as pas besoin d'être parfait demain. Juste 1% meilleur qu'hier. Les petites améliorations constantes créent l'excellence.",
      examples: [
        "📈 Améliore 1 processus par semaine",
        "⚡ Automatise 1 tâche répétitive par mois",
        "🎯 Fixe-toi 1 micro-objectif quotidien",
        "🔄 365 jours × 1% = Expert de classe mondiale"
      ],
      impact: "1% × 365 jours = 37x meilleur en 1 an",
      aiTip: "💡 Prompt IA: 'Identifie 3 micro-optimisations que je peux faire cette semaine sur [processus]'"
    },
    {
      id: 8,
      title: "Simplicité au Service du Client",
      subtitle: "Clarté = Pouvoir Décisionnel",
      icon: Zap,
      color: "amber",
      gradient: "from-amber-500 to-yellow-500",
      description: "Notre mission: rendre le complexe simple. Un client qui comprend = un client qui décide avec confiance.",
      examples: [
        "🎯 Explique en langage client, pas IT",
        "📊 1 visuel vaut 1000 mots techniques",
        "✅ 3 options max avec pour/contre clairs",
        "⚠️ Si le client doit demander des précisions = trop technique"
      ],
      impact: "Client autonome dans ses décisions = Relation de confiance durable",
      aiTip: "💡 Prompt IA: 'Vulgarise ce concept technique pour un client non-IT avec métaphore simple'"
    },
    {
      id: 9,
      title: "Partage de Connaissances",
      subtitle: "On S'Élève Ensemble",
      icon: Users,
      color: "violet",
      gradient: "from-violet-500 to-fuchsia-500",
      description: "La connaissance qui reste dans ta tête ne sert qu'à toi. Celle que tu partages multiplie la force de toute l'équipe.",
      examples: [
        "💬 Partage tes découvertes en équipe",
        "📚 Crée des guides pour les autres",
        "🎓 Enseigne = Maîtrise approfondie",
        "🤝 Ton succès = succès de l'équipe"
      ],
      impact: "Équipe qui partage = Équipe qui domine",
      aiTip: "💡 Prompt IA: 'Transforme mon apprentissage en mini-formation de 5 min pour l'équipe'"
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full my-8"
        >
          {/* Header PUNCHY */}
          <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white p-8 rounded-t-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="flex items-center gap-4 mb-4">
                <Award className="w-16 h-16" />
                <div>
                  <h2 className="text-4xl font-bold mb-2">Amélioration Continue et Rigueur</h2>
                  <p className="text-2xl text-white/90">9 Standards d'Excellence BZ</p>
                  {lastAnalysisDate && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-purple-200">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mt-6">
                <p className="text-lg font-semibold">
                  🔥 "L'excellence n'est pas un acte, mais une habitude." - Aristote
                </p>
                <p className="text-white/90 mt-2">
                  Ces 9 standards sont notre ADN. Ils nous distinguent. Ils nous élèvent. Ensemble, on vise l'excellence.
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 max-h-[70vh] overflow-y-auto">
            {/* Standards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
              {standards.map((standard, index) => {
                const Icon = standard.icon;
                return (
                  <motion.div
                    key={standard.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all hover:scale-105 hover:border-orange-300"
                  >
                    {/* Header */}
                    <div className={`bg-gradient-to-r ${standard.gradient} text-white rounded-lg p-4 mb-4`}>
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className="w-8 h-8" />
                        <span className="text-2xl font-bold">#{standard.id}</span>
                      </div>
                      <h3 className="text-xl font-bold">{standard.title}</h3>
                      <p className="text-sm text-white/90">{standard.subtitle}</p>
                    </div>

                    {/* Description */}
                    <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                      {standard.description}
                    </p>

                    {/* Examples */}
                    <div className="space-y-2 mb-4">
                      {standard.examples.map((example, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{example}</span>
                        </div>
                      ))}
                    </div>

                    {/* Impact */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-r-lg p-3 mb-3">
                      <p className="text-sm font-semibold text-green-900">
                        💥 {standard.impact}
                      </p>
                    </div>

                    {/* AI Tip */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3">
                      <p className="text-xs text-purple-900 font-medium">
                        {standard.aiTip}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Section Motivation Finale */}
            <div className="bg-gradient-to-r from-orange-100 via-red-100 to-pink-100 rounded-xl p-8 border-4 border-orange-300">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Rocket className="w-10 h-10 text-orange-600" />
                Notre Engagement Excellence
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <h4 className="text-xl font-bold text-orange-600 mb-3">🎯 Notre Mission</h4>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Simplifier le complexe pour que nos clients prennent les meilleures décisions pour leur entreprise.</strong>
                  </p>
                  <p className="text-gray-600 mt-3 text-sm">
                    Chaque standard nous rapproche de cette mission. L'IA amplifie notre impact, mais c'est notre rigueur qui fait la différence.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <h4 className="text-xl font-bold text-red-600 mb-3">⚡ L'Effet Composé</h4>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>1% mieux chaque jour = 37× meilleur en 1 an</strong>
                  </p>
                  <p className="text-gray-600 mt-3 text-sm">
                    Ces 9 standards ne sont pas des objectifs lointains. Ce sont des actions quotidiennes. Commence aujourd'hui. Améliore-toi demain. Domine après-demain.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg p-6 text-center">
                <p className="text-2xl font-bold mb-2">
                  🔥 "Excellence is not a destination, it's a continuous journey."
                </p>
                <p className="text-lg text-white/90">
                  - BZ inc. Culture
                </p>
              </div>
            </div>

            {/* RACI pour Amélioration Continue */}
            <div className="mt-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border-2 border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-7 h-7 text-blue-600" />
                Matrice RACI - Culture d'Excellence
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="p-3 text-left rounded-tl-lg">Activité</th>
                      <th className="p-3 text-center">Prime / Lead</th>
                      <th className="p-3 text-center">Équipe TI</th>
                      <th className="p-3 text-center">Direction BZ</th>
                      <th className="p-3 text-center rounded-tr-lg">Client</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    <tr className="border-b hover:bg-blue-50">
                      <td className="p-3 font-semibold">Documentation quotidienne</td>
                      <td className="p-3 text-center bg-green-100 font-bold text-green-800">R</td>
                      <td className="p-3 text-center bg-green-100 font-bold text-green-800">R</td>
                      <td className="p-3 text-center bg-blue-100 font-bold text-blue-800">A</td>
                      <td className="p-3 text-center bg-gray-100 font-bold text-gray-600">I</td>
                    </tr>
                    <tr className="border-b hover:bg-blue-50">
                      <td className="p-3 font-semibold">Bilans post-projet</td>
                      <td className="p-3 text-center bg-blue-100 font-bold text-blue-800">A</td>
                      <td className="p-3 text-center bg-yellow-100 font-bold text-yellow-800">C</td>
                      <td className="p-3 text-center bg-yellow-100 font-bold text-yellow-800">C</td>
                      <td className="p-3 text-center bg-gray-100 font-bold text-gray-600">I</td>
                    </tr>
                    <tr className="border-b hover:bg-blue-50">
                      <td className="p-3 font-semibold">Formation continue IA</td>
                      <td className="p-3 text-center bg-green-100 font-bold text-green-800">R</td>
                      <td className="p-3 text-center bg-green-100 font-bold text-green-800">R</td>
                      <td className="p-3 text-center bg-blue-100 font-bold text-blue-800">A</td>
                      <td className="p-3 text-center bg-gray-100 font-bold text-gray-600">-</td>
                    </tr>
                    <tr className="border-b hover:bg-blue-50">
                      <td className="p-3 font-semibold">Partage de connaissances</td>
                      <td className="p-3 text-center bg-blue-100 font-bold text-blue-800">A</td>
                      <td className="p-3 text-center bg-green-100 font-bold text-green-800">R</td>
                      <td className="p-3 text-center bg-yellow-100 font-bold text-yellow-800">C</td>
                      <td className="p-3 text-center bg-gray-100 font-bold text-gray-600">-</td>
                    </tr>
                    <tr className="hover:bg-blue-50">
                      <td className="p-3 font-semibold">Amélioration processus</td>
                      <td className="p-3 text-center bg-green-100 font-bold text-green-800">R</td>
                      <td className="p-3 text-center bg-yellow-100 font-bold text-yellow-800">C</td>
                      <td className="p-3 text-center bg-blue-100 font-bold text-blue-800">A</td>
                      <td className="p-3 text-center bg-gray-100 font-bold text-gray-600">I</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 flex gap-6 text-xs text-gray-600">
                <div><span className="font-bold text-green-800">R</span> = Responsible (Réalise)</div>
                <div><span className="font-bold text-blue-800">A</span> = Accountable (Approuve)</div>
                <div><span className="font-bold text-yellow-800">C</span> = Consulted (Consulté)</div>
                <div><span className="font-bold text-gray-600">I</span> = Informed (Informé)</div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

